import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Import Sanity client and urlFor
import { PortableText } from '@portabletext/react'; // Import PortableText

// Consistent Loading Spinner
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div>
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Consistent Error Message
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);

// Custom Portable Text components for the warning link
const ptComponents = {
  marks: {
    link: ({ children, value }) => {
      const rel = !value.href.startsWith('/') ? 'noopener noreferrer' : undefined;
      // Apply specific styling for the email link in the warning
      const className = 'text-[#691e31] hover:text-[#8a2c43] transition-colors font-medium';
      return (
        <a href={value.href} target={rel ? '_blank' : undefined} rel={rel} className={className}>
          {children}
        </a>
      );
    },
  },
};

const ESL = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      try {
        const query = `*[_type == "eslPage" && _id == "eslPageContent"][0] {
          bannerTitle,
          bannerImage {
            asset->{
              _id,
              url
            }
          },
          introText,
          importantDatesTitle,
          importantDatesItems,
          identificationTitle,
          identificationBody,
          identificationWarning,
          testingTitle,
          testingLinkButton { text, url },
          proficiencyTitle,
          proficiencyIntro,
          proficiencyLevels[]{
            _key,
            levelName,
            description
          }
        }`;
        const data = await client.fetch(query);
        if (data) {
          setPageData(data);
        } else {
          setError('ESL Page content not found.');
        }
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch ESL page data:", err);
        setError(err.message || 'Failed to load page content. Please try again later.');
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay - Dynamic */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {pageData.bannerImage?.asset?.url ? (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 English Language Learners'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
          ) : (
             <div className="absolute inset-0 w-full h-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-500">Banner Image Missing</span>
            </div> 
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'English Language Learners'} {/* Dynamic */}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* Introduction - Dynamic */}
              {pageData.introText && (
                <div>
                  {/* Use prose for potential styling consistency */} 
                  <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                    <PortableText value={pageData.introText} />
                  </div>
                </div>
              )}
              
              {/* Timeline Information - Dynamic */}
              {pageData.importantDatesTitle && pageData.importantDatesItems?.length > 0 && (
                <div className="bg-[#2a4865]/5 p-6 rounded-lg border-l-4 border-[#2a4865]">
                  <h3 className="text-xl font-bold text-[#2a4865] mb-4 relative after:content-[''] after:absolute after:w-12 after:h-1 after:bg-yellow-500 after:-bottom-2 after:left-0">
                    {pageData.importantDatesTitle}
                  </h3>
                  <ul className="list-none space-y-3 mt-6">
                    {pageData.importantDatesItems.map((item, index) => (
                      <li key={index} className="flex items-start">
                         {/* Keep exact icon */} 
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 mt-0.5 text-[#691e31] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Identification Process - Dynamic */}
              {pageData.identificationTitle && (
                <div>
                  <h3 className="text-2xl font-bold text-[#2a4865] mb-6 relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                    {pageData.identificationTitle}
                  </h3>
                  
                  {pageData.identificationBody && (
                    <div className="mt-8 space-y-4 prose max-w-none text-gray-700 leading-relaxed">
                       <PortableText value={pageData.identificationBody} />
                    </div>
                  )}
                  
                  {pageData.identificationWarning && (
                    <div className="bg-yellow-50 p-5 rounded-lg border-l-4 border-yellow-400 mt-6">
                      <div className="flex items-start">
                         {/* Keep exact icon */} 
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-yellow-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="text-gray-700">
                           <PortableText value={pageData.identificationWarning} components={ptComponents} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Tests Information - Dynamic */}
              {pageData.testingTitle && pageData.testingLinkButton?.url && (
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-bold text-[#2a4865] mb-4 relative after:content-[''] after:absolute after:w-12 after:h-1 after:bg-yellow-500 after:-bottom-2 after:left-0">
                    {pageData.testingTitle}
                  </h3>
                  
                  <div className="flex justify-center mt-6">
                    <a 
                      href={pageData.testingLinkButton.url}
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center bg-[#691e31] text-white py-3 px-6 rounded-full hover:bg-[#7f2a3e] transition-colors font-medium"
                    >
                      <span>{pageData.testingLinkButton.text || 'Learn More'}</span>
                       {/* Keep exact icon */} 
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>
              )}
              
              {/* Proficiency Levels - Dynamic */}
              {pageData.proficiencyTitle && pageData.proficiencyLevels?.length > 0 && (
                <div>
                  <h3 className="text-2xl font-bold text-[#2a4865] mb-6 relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                    {pageData.proficiencyTitle}
                  </h3>
                  
                  {pageData.proficiencyIntro && (
                    <div className="mt-8 prose max-w-none text-gray-700 leading-relaxed mb-6">
                      <PortableText value={pageData.proficiencyIntro} />
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {pageData.proficiencyLevels.map(level => (
                      <div key={level._key} className={`bg-[#2a4865]/5 rounded-lg p-5 border-t-4 border-[#2a4865] ${level.levelName.toLowerCase().includes('commanding') ? 'md:col-span-2' : ''}`}> {/* Handle commanding span */} 
                        <h4 className="font-bold text-[#2a4865] mb-2">{level.levelName}</h4>
                        <p className="text-gray-600">{level.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ESL; 